import { useMemo, useState } from 'react';

import { useRealtime } from '../hooks/useRealtime';
import type { RealtimeEnvelope } from '../lib/realtime';
import type { PositionItem } from '../lib/types';

export interface MarketTick {
  market_id: string;
  symbol: string;
  oracle: number;
  prevOracle: number;
  /** Price-to-beat — the epoch's locked target the asset must clear. */
  ptb: number;
  /** UP/DOWN outcome token prices (0..1 ≈ implied probability). */
  upBid: number;
  downBid: number;
  /** Rolling oracle history for the per-market sparkline. */
  history: number[];
  /** Rolling UP-probability (0..100) history — the Polymarket-style
   *  "chance" chart. */
  upHistory: number[];
}

const HISTORY_CAP = 48;

/**
 * Subscribe to the `global:price` oracle stream and keep the latest tick
 * per market_id — oracle price, PTB target, UP/DOWN token prices, and a
 * rolling price history for the sparkline. Drives the live PM markets
 * grid.
 */
export function useLiveMarkets(): Record<string, MarketTick> {
  const [ticks, setTicks] = useState<Record<string, MarketTick>>({});
  useRealtime('global:price', (env: RealtimeEnvelope) => {
    const p = env.payload as {
      market_id?: string;
      symbol?: string;
      oracle_price?: string;
      ptb?: string;
      up_bid?: string;
      down_bid?: string;
    };
    const id = p.market_id;
    const oracle = Number(p.oracle_price);
    if (!id || !p.symbol || !Number.isFinite(oracle)) return;
    setTicks((prev) => {
      const existing = prev[id];
      const history = [...(existing?.history ?? []), oracle].slice(-HISTORY_CAP);
      const upBid = Number(p.up_bid ?? existing?.upBid ?? 0);
      const upHistory = [...(existing?.upHistory ?? []), upBid * 100].slice(
        -HISTORY_CAP,
      );
      return {
        ...prev,
        [id]: {
          market_id: id,
          symbol: p.symbol as string,
          oracle,
          prevOracle: existing?.oracle ?? oracle,
          ptb: Number(p.ptb ?? existing?.ptb ?? oracle),
          upBid,
          downBid: Number(p.down_bid ?? existing?.downBid ?? 0),
          history,
          upHistory,
        },
      };
    });
  });
  return ticks;
}

export type EquityRange = '24h' | '7d' | '30d' | 'all';

const RANGE_MS: Record<EquityRange, number> = {
  '24h': 24 * 3600_000,
  '7d': 7 * 24 * 3600_000,
  '30d': 30 * 24 * 3600_000,
  all: Number.POSITIVE_INFINITY,
};

/**
 * Cumulative realized-PnL equity curve from the user's closed positions
 * (up to the positions-endpoint limit, ~100 — far more than the 20-item
 * activity page), restricted to the chosen window. Returns the polyline
 * points plus the net realized PnL over the window.
 */
export function useEquitySeries(
  closed: PositionItem[] | undefined,
  range: EquityRange,
  nowMs: number,
): { points: number[]; change: number } {
  return useMemo(() => {
    const cutoff = nowMs - RANGE_MS[range];
    const trades = (closed ?? [])
      .filter((p) => p.realized_pnl_usdc != null && p.closed_at != null)
      .map((p) => ({
        ts: Date.parse(p.closed_at as string),
        pnl: Number(p.realized_pnl_usdc ?? 0),
      }))
      .filter((t) => Number.isFinite(t.ts) && Number.isFinite(t.pnl) && t.ts >= cutoff)
      .sort((a, b) => a.ts - b.ts);

    if (trades.length === 0) return { points: [], change: 0 };
    let acc = 0;
    const points = [0];
    for (const t of trades) {
      acc += t.pnl;
      points.push(acc);
    }
    return { points, change: acc };
  }, [closed, range, nowMs]);
}
